import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2, Phone } from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';

interface CredentialsStepProps {
  initialPhone: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onSubmit: (phone: string) => void;
}

const PHONE_REGEX = /^05\d{8}$/;

const CredentialsStep: React.FC<CredentialsStepProps> = ({
  initialPhone,
  isSubmitting,
  errorMessage,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const Forward = dir === 'rtl' ? ArrowLeft : ArrowRight;

  const [phone, setPhone] = useState(initialPhone);
  const [touched, setTouched] = useState(false);

  const phoneIsValid = PHONE_REGEX.test(phone.trim());
  const canSubmit = phoneIsValid && !isSubmitting;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit(phone.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="text-center mb-2">
        <h2 className="text-2xl font-bold text-gray-900">
          {t('partnerAuth.credentials.heading')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('partnerAuth.credentials.subheading')}
        </p>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
          {t('partnerAuth.credentials.phoneLabel')}
        </label>
        <div className="relative">
          <span className="absolute inset-y-0 start-3 flex items-center text-gray-400 pointer-events-none">
            <Phone className="h-4 w-4" />
          </span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            dir="ltr"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0501234567"
            className={`w-full ps-10 pe-3 py-3 rounded-xl border-2 bg-gray-50 outline-none transition-colors ${
              touched && !phoneIsValid
                ? 'border-red-300 focus:border-red-400 bg-red-50'
                : 'border-gray-200 focus:border-haat-red-500 focus:bg-white'
            }`}
          />
        </div>
        {touched && !phoneIsValid && (
          <p className="text-xs text-red-600 mt-1.5">
            {t('partnerAuth.credentials.phoneError')}
          </p>
        )}
      </div>

      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700"
        >
          {errorMessage}
        </motion.div>
      )}

      <motion.button
        type="submit"
        disabled={!canSubmit}
        whileHover={canSubmit ? { scale: 1.01 } : undefined}
        whileTap={canSubmit ? { scale: 0.98 } : undefined}
        className="w-full rounded-2xl bg-gradient-to-r from-haat-red-500 to-haat-red-700 text-white font-semibold py-3 flex items-center justify-center gap-2 shadow-lg shadow-haat-red-200/60 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            {t('partnerAuth.credentials.continue')}
            <Forward className="h-4 w-4" />
          </>
        )}
      </motion.button>
    </form>
  );
};

export default CredentialsStep;
